import { useMutation } from "@tanstack/react-query";
import {
  DEFAULT_TRADES_ENDPOINT,
  makeTradesUrl,
  type Trade,
  type TradesResponse,
} from "../components/TradeSelector";

interface UseFetchTradesOptions<T = Trade[]> {
  site: string;
  vertical: string;
  tradesEndpoint?: string;
  searchScope?: string;
  variant?: "default" | "ai";
  primaryDetail?: boolean;
  transformResults?: (trades: Trade[]) => T;
  onError?: (error: Error) => void;
}

/** Return type from useFetchTrades mutation including search metadata */
export type FetchTradesResult<T> = {
  results: T;
  searchId?: string;
};

/**
 * Custom hook for fetching trades using TanStack Query.
 * This hook standardizes the trade fetching logic across components.
 */
export const useFetchTrades = <T = Trade[]>(
  options: UseFetchTradesOptions<T>,
) => {
  const {
    site,
    vertical,
    tradesEndpoint = DEFAULT_TRADES_ENDPOINT,
    searchScope,
    variant = "default",
    primaryDetail,
    transformResults,
    onError,
  } = options;

  const mutation = useMutation<FetchTradesResult<T>, Error, string>({
    mutationFn: async (query: string) => {
      const attemptFetch = async (
        fetchVariant: "default" | "ai",
      ): Promise<TradesResponse> => {
        const url = makeTradesUrl({
          site,
          vertical,
          searchScope,
          query,
          tradesEndpoint,
          variant: fetchVariant,
          primaryDetail,
        });

        const response = await fetch(url);

        if (!response.ok) {
          throw new Error("Network response was not ok");
        }

        try {
          // Attempt to parse and return JSON response
          // because we use a 200 status code even for bad results
          const json = (await response.json()) as TradesResponse;
          return json;
        } catch (error) {
          console.error("Failed to parse Trade API response as JSON:", error);
          throw new Error("Unable to fetch trades at this time.");
        }
      };

      try {
        // Initial request with provided variant
        const data = await attemptFetch(variant);

        // Check if AI variant returned empty results
        if (variant === "ai" && (!data.results || data.results.length === 0)) {
          // Attempt fallback to classic API - use fallback's search_id
          const fallbackData = await attemptFetch("default");
          return {
            results: transformResults
              ? transformResults(fallbackData.results)
              : (fallbackData.results as T),
            searchId: fallbackData.search_id,
          };
        }

        return {
          results: transformResults
            ? transformResults(data.results)
            : (data.results as T),
          searchId: data.search_id,
        };
      } catch (error) {
        // If AI variant failed, try fallback to classic API
        if (variant === "ai") {
          const fallbackData = await attemptFetch("default");
          return {
            results: transformResults
              ? transformResults(fallbackData.results)
              : (fallbackData.results as T),
            searchId: fallbackData.search_id,
          };
        }

        // Re-throw error for non-AI variants or if fallback also failed
        throw error;
      }
    },
  });

  // Wrap mutate to include onError callback for v5 compatibility
  const originalMutate = mutation.mutate;
  const wrappedMutate = (query: string) => {
    originalMutate(query, {
      onError: (error: Error) => {
        console.error("Failed to fetch trades:", error);
        onError?.(error);
      },
    });
  };

  return {
    ...mutation,
    mutate: wrappedMutate,
  };
};
